import { useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';

// ============================================================
// useChatPoll
// Owns: fetchAll, 3s polling interval, message scroll,
//       section scroll, isFetching guard, exitedSessionRef.
// Returns: fetchAll, isFetching, messagesEnd, sectionEnd, exitedSessionRef
// ============================================================
export default function useChatPoll({
  matchId, token, activeSection,
  setServerState, setChatClosed, setLoading,
  messageCount, activeGameSessionFsmState,
}) {
  const isFetching          = useRef(false);
  const messagesEnd         = useRef(null);
  const sectionEnd          = useRef(null);
  const exitedSessionRef    = useRef(null);
  const messagesContainer   = useRef(null);

  const fetchAll = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    try {
      const res = await axios.get(`${API_BASE}/chat/${matchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServerState(res.data);
      // Stamp read — fire and forget, no await
      axios.patch(`${API_BASE}/chat/${matchId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    } catch (err) {
      if (err.response?.status === 404) setChatClosed(true);
    } finally {
      isFetching.current = false;
      setLoading(false);
    }
  }, [matchId, token, setServerState, setChatClosed, setLoading]);

  // Polling
  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 3000);
    return () => clearInterval(t);
  }, [fetchAll]);

  // Scroll to latest message when new message arrives or game state changes
  useEffect(() => {
    if (messagesContainer.current) {
      messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight;
    }
  }, [messageCount, activeGameSessionFsmState]);

  // Scroll to bottom of active section when tab changes
  useEffect(() => {
    if (activeSection) setTimeout(() => sectionEnd.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [activeSection]);

  return { fetchAll, isFetching, messagesEnd, sectionEnd, exitedSessionRef, messagesContainer };
}
